import {fromJS, List} from 'immutable'
import {get} from '../../util/http'
import * as schema from '../schema'
import {normalize, denormalize} from 'normalizr'
import {getBreadcrumbsFromTopic} from '../../core/components/breadcrumbs/BreadCrumbs'
import isEmpty from 'lodash/isEmpty'
import { createSelector } from 'reselect'
import {distanceInWords, isEqual, addDays} from 'date-fns'

/**
 * Actions
 */
const ATTEMPT_GET_JOURNEY = 'expii/journey/ATTEMPT_GET_JOURNEY'
export const PROCESS_JOURNEY = 'expii/journey/PROCESS_JOURNEY'
const RESET_JOURNEY = 'expii/journey/RESET_JOURNEY'
const PROCESS_ERROR_MESSAGE = 'expii/journey/PROCESS_ERROR_MESSAGE'


/**
 * Reducer
 */
const defaultJourneyState = fromJS({
  isFetching: false,
  journey: [],
  nextPage: 1,
  totalNum: 0,
  errorMessage: null,
})

function merger(a, b) {
  if (List.isList(a) && List.isList(b)) {
    return a.concat(b)
  }
  if (a && a.mergeWith) {
    return a.mergeWith(merger, b)
  }
  return b
}

export default function reducer(state = defaultJourneyState, action = {}) {
  switch (action.type) {
  case ATTEMPT_GET_JOURNEY:
    return state.withMutations(s => (
      s.set('isFetching', true).set('errorMessage',null)
    ))
  case PROCESS_JOURNEY:
    if (action.payload.withNextPage) {
      return state.withMutations(s =>
        s.mergeWith(merger, fromJS({
          journey: action.payload.journey,
          totalNum: action.payload.totalNum,
          isFetching: false,
        })).update('nextPage', page => page + 1)
      )
    } else {
      return state.mergeWith(merger, fromJS({
        journey: action.payload.journey,
        totalNum: action.payload.totalNum,
        isFetching: false,
      }))
    }
  case PROCESS_ERROR_MESSAGE:
    return state.merge(fromJS({
      errorMessage: action.errorMessage,
      isFetching: false
    }))
  case RESET_JOURNEY:
    return defaultJourneyState
  default:
    return state
  }
}

/**
 * Action Creators
 */
export function attemptGetJourney(withNextPage=false) {
  return { type: ATTEMPT_GET_JOURNEY, withNextPage }
}

export function processJourney(profile_data) {
  return { type: PROCESS_JOURNEY, payload: profile_data }
}

export function resetJourney() {
  return { type: RESET_JOURNEY }
}

export function processErrorMessage(errorMessage) {
  return {type: PROCESS_ERROR_MESSAGE, errorMessage}
}

/**
 * Side Effects
 */
export function getJourney(withNextPage=false) {
  return (dispatch, getState) => {
    dispatch(attemptGetJourney(withNextPage))
    const page = withNextPage ? getState().getIn(['journey','nextPage']) : 0
    return get(`/api/v2/user/journey/${page}/`)
      .then(json => {
        let {success, output} = json
        let {journey, totalNum} = output
        journey = journey.filter((j)=>{
          if (parseInt(j.topic.id) === 0) { //Filter out Universe.
            return false
          }
          return true
        })
        const normalized = normalize(journey, schema.journey)
        json = {
          success:success,
          journey:normalized.result,
          withNextPage,
          totalNum: totalNum,
          entities:normalized.entities
        }
        dispatch(processJourney(json))
      }
      ).catch(json => {
        console.error(json)
        dispatch(processErrorMessage(json.error_message))
      })
  }
}

/**
 * Selectors
 */

const selectNormalizedJourney = (state) => {
  return state.getIn(['journey','journey'])
}
/*const selectIsFetching = (state) => {
  return state.getIn(['journey','isFetching'])
}*/

const getRecentSubjects = (journey) => {
  const recentSubjects = []
  const recentSubjectsSet = new Set()
  if (journey.length) {
    for (let j of journey) {
      if (recentSubjects.length >= 5) {
        return recentSubjects
      }
      const topic = j.topic
      const breadcrumbs = getBreadcrumbsFromTopic(topic)
      const baseTopic = breadcrumbs.length ? breadcrumbs[0] : topic
      if (!recentSubjectsSet.has(baseTopic.id)) {
        recentSubjects.push(baseTopic)
        recentSubjectsSet.add(baseTopic.id)
      }
    }
  }
  return recentSubjects
}

/*const getRecentSubjectsImmutable = (journey) => {
  let recentSubjects = []
  const recentSubjectsSet = new Set()
  function _getBreadcrumbsFromTopic(topic) {
    let breadcrumbTopics = []
    if (topic.get('depth') > 2) {
      breadcrumbTopics.push(topic.get('parent'))
      if (topic.getIn(['parent', 'depth']) > 2) {
        let tempTopic = topic.get('parent')
        while (tempTopic.get('depth') > 2) {
          tempTopic = tempTopic.get('parent')
        }
        if (tempTopic.get('id') !== topic.getIn(['parent', 'id'])) {
          breadcrumbTopics = [tempTopic, topic.get('parent')]
        }
      }
    }
    return fromJS(breadcrumbTopics)
  }
  if (journey.size) {
    journey.forEach(j => {
      if (recentSubjects.size >= 5) {
        return false
      }
      const topic = j.get('topic')
      const breadcrumbs = _getBreadcrumbsFromTopic(topic)
      const baseTopic = breadcrumbs.size ? breadcrumbs.get(0) : topic
      if (!recentSubjectsSet.has(baseTopic.get('id'))) {
        recentSubjects.push(baseTopic)
        recentSubjectsSet.add(baseTopic.get('id'))
      }
    })
  }
  return fromJS(recentSubjects)
}*/

const bucketJourneyByDate = (journey) => {
  const chunks = {}
  const ordering = []
  //const dateFormat = 'ddd, DD MMM YYYY hh:mm:ss '

  let today = new Date()
  today.setHours(0,0,0,0)
  let yesterday = new Date()
  yesterday.setDate(yesterday.getDate() - 1)
  yesterday.setHours(0,0,0,0)

  for (let j of journey) {
    const {day, ...jEntry} = j
    const date = addDays(new Date(day), 1)
    date.setHours(0,0,0,0)
    let timeago = distanceInWords(today, date, {addSuffix: true})
    if (isEqual(today, date)){
      timeago = 'Today'
    }
    else if (isEqual(yesterday, date)){
      timeago = 'Yesterday'
    }
    if (!(Object.prototype.hasOwnProperty.call(chunks,timeago))) {
      chunks[timeago] = []
      ordering.push(timeago)
    }
    chunks[timeago].push(jEntry)
  }
  return {chunks,ordering}
}

/*const bucketJourneyByDateImmutable = (journey) => {
  let chunks = {}
  let ordering = []
  const dateFormat = 'ddd, DD MMM YYYY hh:mm:ss '

  let today = new Date()
  today.setHours(0,0,0,0)
  let yesterday = new Date()
  yesterday.setDate(yesterday.getDate() - 1)
  yesterday.setHours(0,0,0,0)

  journey.forEach(j => {
    const day = j.get('day')
    const date = addDays(new Date(day), 1)
    date.setHours(0,0,0,0)
    let timeago = distanceInWords(today, date, {addSuffix: true})
    if (isEqual(today, date)){
      timeago = 'Today'
    }
    else if (isEqual(yesterday, date)){
      timeago = 'Yesterday'
    }
    if (!(chunks.hasOwnProperty(timeago))){
      chunks[timeago] = []
      ordering.push(timeago)
    }
    chunks[timeago].push(j)
  })
  return fromJS({chunks,ordering})
}*/

const selectEntities = state => {
  return state.getIn(['entities'])
}
export const selectJourney = createSelector(
  [selectNormalizedJourney, selectEntities],
  (journey, entities) => {
    const denormJourney = denormalize(journey.toJS(), schema.journey, entities.toJS())
    const recentSubjects = getRecentSubjects(denormJourney)
    const bucketedJourney = denormJourney.length ? bucketJourneyByDate(denormJourney) : {}
    const didGetJourney = !isEmpty(denormJourney)

    // const denormJourney = denormalize(journey, schema.journey, entities);
    // const recentSubjects = getRecentSubjectsImmutable(denormJourney);
    // const bucketedJourney = denormJourney.size ? bucketJourneyByDateImmutable(denormJourney) : {};
    // const didGetJourney = !denormJourney.isEmpty()
    return {
      bucketedJourney,
      recentSubjects,
      didGetJourney,
    }
  })



// WEBPACK FOOTER //
// ./src/redux/modules/journey.js